const SCROLL_ANIMATION_TRIGGER_CLASSNAME = 'scroll-trigger';
const SCROLL_ANIMATION_OFFSCREEN_CLASSNAME = 'scroll-trigger--offscreen';
const SCROLL_ZOOM_IN_TRIGGER_CLASSNAME = 'animate--zoom-in';
const SCROLL_ANIMATION_CANCEL_CLASSNAME = 'scroll-trigger--cancel';

class ScrollRevealAnimation {
  constructor(root = document, isDesignModeEvent = false) {
    this.root = this.normalizeRoot(root);
    this.isDesignModeEvent = isDesignModeEvent;
    this.elements = Array.from(this.root.getElementsByClassName(SCROLL_ANIMATION_TRIGGER_CLASSNAME));
  }

  init() {
    if (this.elements.length === 0) return;

    if (this.isDesignModeEvent) {
      this.elements.forEach((element) => {
        element.classList.add('scroll-trigger--design-mode');
      });
      return;
    }

    const observer = new IntersectionObserver((elements) => this.onIntersection(elements, observer), {
      rootMargin: '0px 0px -50px 0px',
    });

    this.elements.forEach((element) => observer.observe(element));
  }

  onIntersection(elements, observer) {
    elements.forEach((element, index) => {
      if (element.isIntersecting) {
        const elementTarget = element.target;
        if (elementTarget.classList.contains(SCROLL_ANIMATION_OFFSCREEN_CLASSNAME)) {
          elementTarget.classList.remove(SCROLL_ANIMATION_OFFSCREEN_CLASSNAME);
          if (elementTarget.hasAttribute('data-cascade'))
            elementTarget.setAttribute('style', `--animation-order: ${index};`);
        }
        observer.unobserve(elementTarget);
      } else {
        element.target.classList.add(SCROLL_ANIMATION_OFFSCREEN_CLASSNAME);
        element.target.classList.remove(SCROLL_ANIMATION_CANCEL_CLASSNAME);
      }
    });
  }

  normalizeRoot(root) {
    if (typeof root !== 'string') return root;

    const parsedRoot = document.createElement('div');
    parsedRoot.innerHTML = root;
    return parsedRoot;
  }
}

function initializeScrollAnimationTrigger(rootEl = document, isDesignModeEvent = false) {
  new ScrollRevealAnimation(rootEl, isDesignModeEvent).init();
}

class ScrollZoomAnimation {
  constructor(root = document) {
    this.root = root;
    this.elements = Array.from(this.root.getElementsByClassName(SCROLL_ZOOM_IN_TRIGGER_CLASSNAME));
    this.scaleAmount = 0.2 / 100;
  }

  init() {
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches || this.elements.length === 0) return;

    this.elements.forEach((element) => {
      let elementIsVisible = false;
      const observer = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          elementIsVisible = entry.isIntersecting;
        });
      });
      observer.observe(element);

      this.updateZoomRatio(element);

      window.addEventListener(
        'scroll',
        throttle(() => {
          if (!elementIsVisible) return;

          this.updateZoomRatio(element);
        }),
        { passive: true }
      );
    });
  }

  updateZoomRatio(element) {
    element.style.setProperty('--zoom-in-ratio', 1 + this.scaleAmount * this.percentageSeen(element));
  }

  percentageSeen(element) {
    const viewportHeight = window.innerHeight;
    const scrollY = window.scrollY;
    const elementPositionY = element.getBoundingClientRect().top + scrollY;
    const elementHeight = element.offsetHeight;

    if (elementPositionY > scrollY + viewportHeight) {
      return 0;
    } else if (elementPositionY + elementHeight < scrollY) {
      return 100;
    }

    const distance = scrollY + viewportHeight - elementPositionY;
    const percentage = distance / ((viewportHeight + elementHeight) / 100);
    return Math.round(percentage);
  }
}

function initializeScrollZoomAnimationTrigger() {
  new ScrollZoomAnimation().init();
}

function percentageSeen(element) {
  return new ScrollZoomAnimation().percentageSeen(element);
}

window.addEventListener('DOMContentLoaded', () => {
  initializeScrollAnimationTrigger();
  initializeScrollZoomAnimationTrigger();
});

if (Shopify.designMode) {
  document.addEventListener('shopify:section:load', (event) => initializeScrollAnimationTrigger(event.target, true));
  document.addEventListener('shopify:section:reorder', () => initializeScrollAnimationTrigger(document, true));
}
